import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Brain, Save, Loader2, ShieldCheck, Sparkles, Cpu, Lock } from "lucide-react";

const PROVIDERS = [
  { id: "gemini", label: "Google Gemini" },
  { id: "openai", label: "OpenAI (ChatGPT)" },
  { id: "anthropic", label: "Anthropic (Claude)" },
];

export default function AiIntegration() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [agent, setAgent] = useState(null);
  const [agentBusy, setAgentBusy] = useState(false);

  const load = () => api.get("/settings/ai-config").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/ai/agent/config").then((r) => setAgent(r.data)).catch(() => {}); }, []);

  const toggleAgent = async () => {
    if (!agent) return;
    setAgentBusy(true);
    try {
      const { data } = await api.put("/ai/agent/config", { enabled: !agent.enabled });
      setAgent({ ...agent, enabled: data.enabled });
      toast.success(data.enabled ? "Otonom asistan açıldı" : "Otonom asistan kapatıldı");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setAgentBusy(false);
  };

  if (!cfg) return <div className="rounded-lg bg-white p-8 text-center text-sm text-slate-400 ring-1 ring-inset ring-slate-200"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>;

  const models = cfg.models?.[cfg.provider] || [];
  const setProvider = (id) => {
    const m = cfg.models?.[id] || [];
    setCfg({ ...cfg, provider: id, model: m.includes(cfg.model) ? cfg.model : (m[0] || cfg.model) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/ai-config", { provider: cfg.provider, model: cfg.model });
      setCfg(data);
      toast.success("AI ayarları kaydedildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="ai-integration-card">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-violet-600"><Brain className="h-5 w-5 text-white" /></div>
        <div>
          <p className="font-heading text-lg font-bold text-slate-900">AI Asistan Sağlayıcısı</p>
          <p className="text-sm text-slate-500">Kullanacağınız yapay zekâ sağlayıcısını ve modelini seçin. Anahtar yönetimi platform tarafındadır.</p>
        </div>
      </div>

      <div data-testid="ai-managed-note" className="mb-4 flex items-start gap-2 rounded-lg bg-violet-50 px-3 py-2.5 text-[12px] leading-snug text-violet-700 ring-1 ring-inset ring-violet-200">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <span><b>Yönetilen anahtar (kontrol platformda).</b> AI, güvenlik ve maliyet kontrolü için yalnızca platformun yönetilen anahtarını kullanır; kendi API anahtarınızı girmeniz gerekmez. Kullanım kontör (kredi) bakiyenizden düşülür.</span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-[11px]">Sağlayıcı</Label>
          <select data-testid="ai-provider" value={cfg.provider} onChange={(e) => setProvider(e.target.value)}
            className="mt-1 h-9 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
            {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px]">Model</Label>
          <select data-testid="ai-model" value={cfg.model} onChange={(e) => setCfg({ ...cfg, model: e.target.value })}
            className="mt-1 h-9 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
            {models.map((m) => <option key={m} value={m}>{m}</option>)}
            {!models.includes(cfg.model) && cfg.model && <option value={cfg.model}>{cfg.model}</option>}
          </select>
        </div>
      </div>

      <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400"><Sparkles className="h-3.5 w-3.5 text-violet-500" /> Önerilen: <b className="text-slate-500">Gemini 3 Flash</b> — hızlı ve ekonomik.</p>

      {agent && (
        <div data-testid="ai-agent-toggle-card" className="mt-4 rounded-lg border border-brand-orange/30 bg-brand-orange/5 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-orange"><Cpu className="h-4 w-4 text-white" /></div>
              <div>
                <p className="text-sm font-bold text-slate-800">Otonom Asistan (Agentic)</p>
                <p className="mt-0.5 text-[11px] leading-snug text-slate-500">Açıkken AI, yetkili kullanıcılar için düşük riskli işleri (stok sorgu, taslak sipariş, ürün/müşteri/sipariş notu) otomatik yapar. Her işlem geri alınabilir ve günlüğe yazılır.</p>
              </div>
            </div>
            <button data-testid="ai-agent-master-toggle" onClick={toggleAgent} disabled={agentBusy} role="switch" aria-checked={agent.enabled}
              className={`relative mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${agent.enabled ? "bg-brand-orange" : "bg-slate-300"}`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${agent.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
          <div className="mt-2 flex items-start gap-2 rounded-md bg-red-50/70 px-2.5 py-1.5 text-[11px] leading-snug text-red-700 ring-1 ring-inset ring-red-100">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" /> <span><b>Kesin Duvar:</b> AI; platform yönetimi, kontör/cüzdan, kullanıcı/rol, güvenlik kayıtları, Z-rapor kilitleri ve fiyat/maliyet gibi kritik alanlara ASLA erişemez.</span>
          </div>
        </div>
      )}

      <Button data-testid="ai-config-save" onClick={save} disabled={saving} className="mt-4 bg-violet-600 text-white hover:bg-violet-700">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Kaydet
      </Button>
    </div>
  );
}
